import { reactive, toRefs } from 'vue'
import { defineStore } from 'pinia'
import { fetchConsultant } from '@/api/authApi'
import type { LocationCountry, RegScenario } from '@/api/types'
import {
  readSessionString,
  writeSessionString,
} from '@/lib/sessionStorage'
import {
  isSimpleRegCountry,
  isSimpleRegScenario,
  SIMPLE_REG_SCENARIO,
} from '@/lib/simpleRegistration'

function sessionBool(key: string, fallback: boolean) {
  const raw = sessionStorage.getItem(key)
  if (raw === null) return fallback
  try {
    return JSON.parse(raw) as boolean
  } catch {
    return fallback
  }
}

export const useRegDataStore = defineStore('regData', () => {
  const state = reactive({
    regScenario: (readSessionString('regScenario', 'default') ||
      'default') as RegScenario,
    location: {
      country: {
        code: '',
        name: '',
        flag: '',
      } as LocationCountry,
      city: '',
      state: '',
    },
    refCode: readSessionString('refCode'),
    autofit: sessionBool('autofit', true),
    subscribe_agree: readSessionString('subscribe_agree'),
    agreementChecked: readSessionString('agreementChecked'),
    processingChecked: readSessionString('processingChecked'),
    memberName: readSessionString('memberName'),
    memberAvatar: readSessionString('memberAvatar'),
    memberId: readSessionString('memberId'),
    memberPhone: '',
    NAME: readSessionString('NAME'),
    SURNAME: readSessionString('SURNAME'),
    OTCHESTVO: readSessionString('OTCHESTVO'),
    EMAIL: readSessionString('EMAIL'),
    PHONE: readSessionString('PHONE'),
    PHONE_CODE: readSessionString('PHONE_CODE'),
    BIRTHDATE: readSessionString('BIRTHDATE'),
    GENDER: (readSessionString('GENDER') || null) as string | null,
    PASSWORD: readSessionString('PASSWORD'),
    responseAboutReg: '',
    ID: readSessionString('ID'),
    newID: '',
    changePassCode: '',
    regByReferer: readSessionString('regByReferer'),
    selectedLanguage: '',
    coords: '',
  })

  try {
    sessionStorage.removeItem('responseAboutReg')
  } catch {}

  function setRegScenario(value: RegScenario) {
    state.regScenario = value
    writeSessionString('regScenario', value)
  }
  function setCountry(value: LocationCountry) {
    state.location.country = { ...value }
  }
  function setCity(value: string) {
    state.location.city = value
  }
  function setState(value: string) {
    state.location.state = value
  }
  function setRefCode(value: string) {
    state.refCode = value
    writeSessionString('refCode', value)
  }
  function setAgreementChecked(value: boolean) {
    state.agreementChecked = value ? 'on' : ''
    writeSessionString('agreementChecked', state.agreementChecked)
  }
  function setProcessingChecked(value: boolean) {
    state.processingChecked = value ? 'on' : ''
    writeSessionString('processingChecked', state.processingChecked)
  }
  function setSubscribeAgree(value: boolean) {
    state.subscribe_agree = value ? 'on' : ''
    writeSessionString('subscribe_agree', state.subscribe_agree)
  }
  function setAutofit(value: boolean) {
    state.autofit = value
    writeSessionString('autofit', String(value))
  }
  function setMemberId(value: string) {
    state.memberId = value
    writeSessionString('memberId', value)
  }
  function setMemberPhone(value: string) {
    state.memberPhone = value
  }
  function setMemberAvatar(value: string) {
    state.memberAvatar = value
    writeSessionString('memberAvatar', value)
  }
  function setMemberName(value: string) {
    state.memberName = value
    writeSessionString('memberName', value)
  }
  function setNAME(value: string) {
    state.NAME = value
    writeSessionString('NAME', value)
  }
  function setSURNAME(value: string) {
    state.SURNAME = value
    writeSessionString('SURNAME', value)
  }
  function setOTCHESTVO(value: string) {
    state.OTCHESTVO = value
    writeSessionString('OTCHESTVO', value)
  }
  function setEMAIL(value: string) {
    state.EMAIL = value
    writeSessionString('EMAIL', value)
  }
  function setPHONE(value: string) {
    state.PHONE = value
    writeSessionString('PHONE', value)
  }
  function setPHONE_CODE(value: string) {
    state.PHONE_CODE = value
    writeSessionString('PHONE_CODE', value)
  }
  function setBIRTHDATE(value: string) {
    state.BIRTHDATE = value
    writeSessionString('BIRTHDATE', value)
  }
  function setPASSWORD(value: string) {
    state.PASSWORD = value
    writeSessionString('PASSWORD', value)
  }
  function setGENDER(value: string | null) {
    state.GENDER = value
    writeSessionString('GENDER', value || '')
  }
  function setResponseAboutReg(value: string) {
    state.responseAboutReg = value
  }
  function setID(value: string) {
    state.ID = value
    writeSessionString('ID', value)
  }
  function setNewID(value: string) {
    state.newID = value
  }
  function setChangePassCode(value: string) {
    state.changePassCode = value
  }
  function setRegByReferer(value: string) {
    state.regByReferer = value
    writeSessionString('regByReferer', value)
  }
  function setSelectedLanguage(value: string) {
    state.selectedLanguage = value
  }
  function setCoords(value: string) {
    state.coords = value
  }

  async function setRefCodeReg(value: string) {
    await getConsultantData({ refCodeValue: value })
  }

  async function getConsultantData({
    refCodeValue = null,
    refererLoginValue = null,
  }: {
    refCodeValue?: string | null
    refererLoginValue?: string | null
  }) {
    try {
      const params = refererLoginValue
        ? { memberId: refererLoginValue }
        : { refCode: refCodeValue || undefined }
      const response = await fetchConsultant(params)
      if (!response.status || !response.result) {
        setRefCode('')
        setRegScenario('default')
        return
      }
      setRefCode(response.result.REF_CODE)
      setMemberId(response.result.MEMBER_ID)
      setMemberAvatar(response.result.AVATAR)
      setMemberName(response.result.NAME)
      setAutofit(false)
      // Не сбрасываем refCodeSimple → refCode на время init (иначе мигает центральный скелетон).
      // Финальный сценарий выставит resolveRegistrationScenario после страны.
      if (!state.regByReferer && !isSimpleRegScenario(state.regScenario)) {
        setRegScenario('refCode')
      }
    } catch (error) {
      console.error(error)
    }
  }

  function resolveRegistrationScenario(options?: {
    countries?: readonly string[]
    countryCode?: string
  }) {
    if (state.regByReferer || !state.refCode) return
    const countryCode =
      options?.countryCode || state.location.country?.code || ''
    if (isSimpleRegCountry(countryCode, options?.countries)) {
      setRegScenario(SIMPLE_REG_SCENARIO)
      return
    }
    setRegScenario('refCode')
  }

  return {
    ...toRefs(state),
    setRegScenario,
    setCountry,
    setCity,
    setState,
    setRefCode,
    setAgreementChecked,
    setProcessingChecked,
    setSubscribeAgree,
    setAutofit,
    setMemberId,
    setMemberPhone,
    setMemberAvatar,
    setMemberName,
    setNAME,
    setSURNAME,
    setOTCHESTVO,
    setEMAIL,
    setPHONE,
    setPHONE_CODE,
    setBIRTHDATE,
    setPASSWORD,
    setGENDER,
    setResponseAboutReg,
    setID,
    setNewID,
    setChangePassCode,
    setRegByReferer,
    setSelectedLanguage,
    setCoords,
    setRefCodeReg,
    getConsultantData,
    resolveRegistrationScenario,
  }
})
